import type {
  CommentResponse,
  CraftProjectResponse,
  CraftRoomPageResponse,
  CraftRoomMaterialsPageResponse,
  CraftRoomResponse,
  GetCommentsResponse,
  GetUserCraftProjectsResponse,
  SecureCraftRoomPageResponse,
  UserCraftProjectResponse,
} from '@mrb91002/shared-types'

import { API_BASE, SECURE_API_BASE } from '../config'

export async function fetchCraftRoomPage(screenName: string): Promise<CraftRoomPageResponse> {
  const resp = await fetch(`${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}`)
  if (!resp.ok) throw new Error('Failed to fetch craft room page')
  return resp.json() as Promise<CraftRoomPageResponse>
}

export async function fetchSecureCraftRoomPage(
  screenName: string,
  idToken: string,
): Promise<SecureCraftRoomPageResponse> {
  const resp = await fetch(`${SECURE_API_BASE}/craft-room/${encodeURIComponent(screenName)}`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
  if (!resp.ok) throw new Error('Failed to fetch secure craft room page')
  return resp.json() as Promise<SecureCraftRoomPageResponse>
}

export async function fetchCraftRoomMaterials(
  screenName: string,
  lastKey: string | null = null,
): Promise<CraftRoomMaterialsPageResponse> {
  let url = `${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}/materials`
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`
  const resp = await fetch(url)
  if (!resp.ok) throw new Error('Failed to fetch craft room materials')
  return resp.json() as Promise<CraftRoomMaterialsPageResponse>
}

export async function fetchCraftRoom(screenName: string): Promise<CraftRoomResponse> {
  const resp = await fetch(`${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}`)
  if (!resp.ok) throw new Error('Failed to fetch craft room')
  const data: CraftRoomPageResponse = await resp.json() as CraftRoomPageResponse
  return data.craftRoom || data
}

export async function fetchCraftProject(projectId: string): Promise<CraftProjectResponse> {
  const resp = await fetch(`${API_BASE}/craft-projects/${encodeURIComponent(projectId)}`)
  if (!resp.ok) throw new Error('Failed to fetch craft project')
  return resp.json() as Promise<CraftProjectResponse>
}

export async function fetchUserCraftProjects(idToken: string): Promise<UserCraftProjectResponse[]> {
  const resp = await fetch(`${API_BASE.replace('/api', '/api/secure')}/projects`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
  if (!resp.ok) throw new Error('Failed to fetch user craft projects')
  const data: GetUserCraftProjectsResponse = await resp.json() as GetUserCraftProjectsResponse
  return data.projects || []
}

export async function fetchPublicCraftProjects(limit: number = 20): Promise<CraftProjectResponse[]> {
  const resp = await fetch(`${API_BASE}/craft-projects/public?limit=${limit}`)
  if (!resp.ok) throw new Error('Failed to fetch public craft projects')
  const data = await resp.json() as { projects: CraftProjectResponse[] }
  return data.projects || []
}

export async function fetchProjectComments(projectId: string): Promise<CommentResponse[]> {
  const resp = await fetch(`${API_BASE}/craft-projects/${encodeURIComponent(projectId)}/comments`)
  if (!resp.ok) throw new Error('Failed to fetch project comments')
  const data: GetCommentsResponse = await resp.json() as GetCommentsResponse
  return data.comments || []
}
